/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { NdaLegalBox } from "./components/NdaMention";
import {
  ArrowRight,
  CheckCircle2,
  BookOpen,
  Award,
  Target,
  Sparkles,
  FileText,
  GraduationCap,
  Lock,
} from "lucide-react";
import { getFormationById } from "./data/formations";
import { FormatriceCard, RecevezBlock, TestimonialsPlaceholder } from "./components/MaryseBlocks";

// ===============================================================
// MGB SCHOOL — Landing dédiée Agrégation / Concours (F16)
// Route : /agregation
// Toutes les données viennent de la formation F16 dans formations.js
// ===============================================================

const AgregationPage = () => {
  const formation = getFormationById("F16");
  if (!formation) return null;

  const stripeLink = `/paiement/${formation.code}`;

  return (
    <div className="pt-20 md:pt-24" data-testid="agregation-page">
      {/* ============ HERO ============ */}
      <section
        className="py-20 md:py-28 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #003087 0%, #1E3A8A 55%, #2196F3 100%)",
        }}
      >
        <div className="absolute -right-40 -top-40 w-[500px] h-[500px] bg-[#F07B1D]/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -left-32 -bottom-40 w-[500px] h-[500px] bg-[#2196F3]/15 rounded-full blur-3xl pointer-events-none"></div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <Badge
                className="bg-[#F07B1D] text-white font-bold mb-5 text-xs uppercase tracking-wider"
                data-testid="agregation-badge"
              >
                🎯 Concours · Agrégation · Masters
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight">
                De la SWOT à l'agrégation,
                <br />
                <span className="text-[#F07B1D]">réussissez vos concours</span>
              </h1>
              <p className="text-lg md:text-xl text-white/85 mb-8 leading-relaxed max-w-2xl">
                Pack Dissertation Management Stratégique — méthodologie complète,
                10 dissertations entièrement rédigées, 10 plans approfondis, 20 sujets
                prioritaires et 6 auteurs majeurs : Schumpeter, Drucker, Mintzberg, Crozier, Castel, Argyris & Schön.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href={stripeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="agregation-hero-cta"
                >
                  <Button className="bg-[#F07B1D] hover:bg-[#d96a14] text-white font-bold px-8 py-6 text-base shadow-lg w-full sm:w-auto">
                    💳 Acheter maintenant — 990 €
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="#agregation-programme">
                  <Button
                    variant="outline"
                    className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-[#003087] font-bold px-8 py-6 text-base w-full sm:w-auto"
                  >
                    Voir le programme
                  </Button>
                </a>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 text-white/80 text-sm">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#F07B1D]" />
                  Accès immédiat PDF
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#F07B1D]" />
                  Paiement unique
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#F07B1D]" />
                  Satisfait ou remboursé 14 jours
                </span>
              </div>
            </div>

            {/* Carte aperçu */}
            <div className="lg:col-span-2">
              <div
                className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-white"
                style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
              >
                <div className="text-xs uppercase tracking-wider text-[#F07B1D] font-bold mb-3">
                  Aperçu du pack
                </div>
                <ul className="space-y-3 text-sm">
                  {[
                    "Méthodologie complète de la dissertation (PDF)",
                    "6 auteurs majeurs expliqués (Schumpeter, Drucker, Mintzberg, Crozier, Castel, Argyris & Schön)",
                    "10 dissertations entièrement rédigées",
                    "10 sujets avec plans approfondis + 20 sujets prioritaires commentés",
                    "Méthode de dissertation pas à pas",
                    "Quiz interactifs + certificat MGB",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#F07B1D] mt-0.5 flex-shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ POURQUOI CE PACK ============ */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="bg-[#2196F3] text-white font-bold mb-4">
              Pourquoi ce pack ?
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003087] mb-4">
              Une méthode claire, des dissertations rédigées
            </h2>
            <p className="text-gray-600">
              Conçu pour les candidats au concours et à l'agrégation, accessible
              dès le lycée. Théories complexes expliquées simplement, exemples
              d'entreprises réelles, plans détaillés.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <BookOpen className="w-7 h-7 text-[#F07B1D]" />,
                title: "Théories décodées",
                desc: "Schumpeter, Drucker, Mintzberg, Crozier, Castel, Argyris & Schön — chaque auteur expliqué avec cas concrets : Apple, Tesla, Amazon, Toyota, Netflix.",
              },
              {
                icon: <Target className="w-7 h-7 text-[#F07B1D]" />,
                title: "Outils stratégiques maîtrisés",
                desc: "SWOT, PESTEL, Porter 5 forces, VRIO, BCG, Ansoff, chaîne de valeur — appliqués pas à pas à des sujets de concours.",
              },
              {
                icon: <Award className="w-7 h-7 text-[#F07B1D]" />,
                title: "Dissertations clés en main",
                desc: "10 dissertations rédigées avec plan dialectique I/II/III, introductions, transitions et conclusions, 10 plans approfondis et 20 sujets prioritaires commentés.",
              },
            ].map((b) => (
              <div
                key={b.title}
                className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-[#F07B1D]/10 flex items-center justify-center mb-4">
                  {b.icon}
                </div>
                <h3 className="font-heading text-lg font-bold text-[#003087] mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ COMPÉTENCES VISÉES ============ */}
      <section className="py-16 bg-[#F5F7FB]">
        <div className="container-custom max-w-4xl">
          <div className="mb-8">
            <Badge className="bg-[#F07B1D] text-white font-bold mb-3">
              Compétences visées
            </Badge>
            <h2 className="font-heading text-3xl font-bold text-[#003087]">
              Ce que vous saurez faire à la fin du pack
            </h2>
          </div>

          <div
            className="italic text-[#1E293B] text-sm md:text-base mb-6 px-5 py-3"
            style={{
              backgroundColor: "#EFF4FF",
              borderLeft: "4px solid #F07B1D",
              borderRadius: "8px",
            }}
          >
            À l'issue de cette formation, vous serez capable de :
          </div>

          <ol className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-100 shadow-sm">
            {formation.competences.map((c, idx) => (
              <li key={c} className="flex items-start gap-3 p-4 md:p-5">
                <span
                  className="shrink-0 w-7 h-7 rounded-full bg-[#2196F3] text-white font-bold flex items-center justify-center text-sm"
                >
                  {idx + 1}
                </span>
                <span className="text-[#1E293B] leading-relaxed">{c}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============ PROGRAMME ============ */}
      <section
        id="agregation-programme"
        className="py-16 bg-white"
      >
        <div className="container-custom max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="bg-[#003087] text-white font-bold mb-3">
              Programme · 5 modules
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003087]">
              Un parcours structuré, du concept à la copie
            </h2>
          </div>

          <div className="space-y-4">
            {formation.programme.map((mod, idx) => (
              <div
                key={mod.title}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#F07B1D]/40 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-extrabold text-white"
                    style={{ background: "#003087" }}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-[#003087] mb-3">
                      {mod.title}
                    </h3>
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                      {mod.items.map((it) => (
                        <li
                          key={it}
                          className="flex items-start gap-2 text-sm text-gray-700"
                        >
                          <CheckCircle2 className="w-4 h-4 text-[#F07B1D] mt-0.5 flex-shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INCLUS ============ */}
      <section className="py-16 bg-[#F5F7FB]">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-10">
            <Badge className="bg-[#2196F3] text-white font-bold mb-3">
              Ce qui est inclus
            </Badge>
            <h2 className="font-heading text-3xl font-bold text-[#003087]">
              Tout ce dont vous avez besoin pour préparer votre concours
            </h2>
          </div>

          <ul className="grid sm:grid-cols-2 gap-3">
            {formation.inclusions.map((it) => (
              <li
                key={it}
                className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-100 text-sm text-gray-700"
              >
                <Sparkles className="w-4 h-4 text-[#F07B1D] mt-0.5 flex-shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ PUBLIC ============ */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#F5F7FB] rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="w-5 h-5 text-[#F07B1D]" />
                <h3 className="font-heading text-lg font-bold text-[#003087]">
                  Pour qui ?
                </h3>
              </div>
              <ul className="space-y-2">
                {formation.publicCible.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#F07B1D] mt-0.5 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#F5F7FB] rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#F07B1D]" />
                <h3 className="font-heading text-lg font-bold text-[#003087]">
                  Prérequis
                </h3>
              </div>
              <ul className="space-y-2">
                {formation.prerequis.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#F07B1D] mt-0.5 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ EXTRAIT GRATUIT ============ */}
      <section className="py-16 bg-white" data-testid="agregation-extrait-gratuit">
        <div className="container-custom max-w-6xl">
          <div className="text-center mb-10">
            <Badge className="bg-[#F07B1D] text-white font-bold mb-3">
              Aperçu gratuit
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003087]">
              📖 Découvrez la qualité du contenu avant d'acheter
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Encadré 1 — Mise en tension */}
            <div
              className="rounded-xl p-5 md:p-6"
              style={{
                backgroundColor: "#E3F2FD",
                border: "1px solid #BFDBFE",
              }}
            >
              <h3 className="font-heading font-bold text-[#F07B1D] mb-3 text-sm">
                Exemple — Sujet : L'agilité est-elle un facteur de performance ?
              </h3>
              <div className="text-xs text-gray-700 space-y-2 leading-relaxed">
                <p className="font-bold text-[#003087]">L'agilité PERMET :</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Innovation rapide (Schumpeter)</li>
                  <li>Adaptation à l'environnement VUCA</li>
                  <li>Mobilisation des équipes (Drucker)</li>
                </ul>
                <p className="font-bold text-[#003087] pt-1">MAIS elle PEUT AUSSI :</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Créer du chaos stratégique (Mintzberg)</li>
                  <li>Générer du burn-out</li>
                  <li>Manquer de cohérence long terme (Mintzberg, stratégie émergente)</li>
                </ul>
                <p className="font-bold text-[#003087] pt-1">DONC la vraie question :</p>
                <p className="italic">
                  « Comment concilier flexibilité et cohérence pour une performance durable ? »
                </p>
              </div>
            </div>

            {/* Encadré 2 — Problématique */}
            <div
              className="rounded-xl p-5 md:p-6"
              style={{
                backgroundColor: "#ECFDF5",
                border: "1px solid #A7F3D0",
              }}
            >
              <h3 className="font-heading font-bold text-[#059669] mb-3 text-sm">
                Exemple de problématique
              </h3>
              <div className="text-xs text-gray-700 space-y-3 leading-relaxed">
                <div>
                  <p className="font-bold text-[#DC2626] mb-1">❌ Mauvaise problématique :</p>
                  <p className="italic">« Qu'est-ce que la compétitivité ? »</p>
                </div>
                <div>
                  <p className="font-bold text-[#059669] mb-1">✅ Bonne problématique :</p>
                  <p className="italic">
                    « Dans un environnement mondialisé et instable, comment les entreprises peuvent-elles améliorer leur performance tout en conservant leur compétitivité durable ? »
                  </p>
                </div>
              </div>
            </div>

            {/* Encadré 3 — Plan dialectique */}
            <div
              className="rounded-xl p-5 md:p-6"
              style={{
                backgroundColor: "#FFF3E8",
                border: "1px solid #FED7AA",
              }}
            >
              <h3 className="font-heading font-bold text-[#F07B1D] mb-3 text-sm">
                Plan dialectique — Sujet : Compétitivité et performance
              </h3>
              <div className="text-xs text-gray-700 space-y-2 leading-relaxed">
                <p className="font-bold text-[#003087]">
                  I. Les facteurs de compétitivité améliorent la performance
                </p>
                <p className="font-bold text-[#003087]">
                  II. Mais cette recherche crée des tensions paradoxales
                </p>
                <p className="font-bold text-[#003087]">
                  III. Vers un modèle hybride et durable
                </p>
              </div>
            </div>
          </div>

          <p className="text-center text-sm italic text-gray-600 mt-8 max-w-2xl mx-auto">
            Ce niveau de détail et de précision est celui de l'ensemble des 10 dissertations corrigées incluses dans la formation.
          </p>
        </div>
      </section>

      {/* ============ CE QUE VOUS RECEVEZ ============ */}
      <section className="py-12 bg-[#F5F7FA]">
        <div className="container-custom max-w-3xl">
          <RecevezBlock formation={formation} />
        </div>
      </section>

      {/* ============ FORMATRICE ============ */}
      <section className="py-12 bg-white">
        <div className="container-custom max-w-3xl">
          <FormatriceCard />
        </div>
      </section>

      {/* ============ FAQ ANTI-OBJECTIONS ============ */}
      <section className="py-16 bg-[#F5F7FA]" data-testid="agregation-faq">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-10">
            <Badge className="bg-[#003087] text-white font-bold mb-3">
              FAQ
            </Badge>
            <h2 className="font-heading text-3xl font-bold text-[#003087]">
              Vos questions, nos réponses
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: "Pourquoi cette formation vaut 990 € ?",
                a: "Parce qu'elle remplace ce que les prépas agrégation facturent 1 800 à 4 150 € avec des résultats comparables. Votre formatrice a elle-même préparé l'agrégation en 2025 et connaît exactement ce que les jurys attendent. Vous accédez à 20 ans d'expérience terrain + une expertise académique complète pour 990 € en accès à vie.",
              },
              {
                q: "Qui a écrit ces dissertations ?",
                a: "Maryse GALITA — ancienne chef d'entreprise transport 20 ans, enseignante Bac Pro OTM, candidate agrégation économie-gestion 2025, formatrice certifiée. Chaque dissertation est rédigée selon les critères exacts des jurys d'agrégation et de concours.",
              },
              {
                q: "Est-ce que ce contenu est vraiment de niveau agrégation ?",
                a: "Maryse GALITA a préparé et passé l'agrégation économie-gestion en 2025. Elle connaît de l'intérieur les exigences des jurys. Les plans, les problématiques et les mises en tension sont construits selon ces exigences précises.",
              },
              {
                q: "Comment savoir si le contenu est de qualité avant d'acheter ?",
                a: "Consultez les exemples gratuits ci-dessus — mise en tension, problématique, plan dialectique. C'est le niveau de l'ensemble des 10 dissertations corrigées.",
              },
              {
                q: "Je peux me faire rembourser ?",
                a: "Oui. Vous disposez de 14 jours pour demander un remboursement complet à contact@mgbschool.fr — sans justification, sans pénalité.",
              },
              {
                q: "Est-ce que MGB School est un vrai organisme de formation ?",
                a: "Oui. MGB School est un organisme de formation déclaré (SASU, SIRET 999 673 635 00010 — NDA 11757727775, déclaration d'activité enregistrée auprès du préfet de la région Île-de-France ; cet enregistrement ne vaut pas agrément de l'État), dispensant des formations conformes au Code du travail. Formatrice professionnelle certifiée.",
              },
            ].map((item) => (
              <details
                key={item.q}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
              >
                <summary
                  className="cursor-pointer px-5 py-4 font-bold text-[#003087] hover:bg-gray-50 transition-colors text-sm md:text-base list-none flex items-center justify-between gap-3"
                  style={{ userSelect: "none" }}
                >
                  <span>{item.q}</span>
                  <span className="text-[#F07B1D] text-lg flex-shrink-0">+</span>
                </summary>
                <div className="px-5 pb-4 text-sm text-gray-700 leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TÉMOIGNAGES ============ */}
      <TestimonialsPlaceholder />

      {/* ============ CTA FINAL ============ */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div
            className="rounded-2xl p-8 md:p-12 max-w-4xl mx-auto text-center"
            style={{
              background:
                "linear-gradient(135deg, #003087 0%, #1E3A8A 60%, #2855B5 100%)",
              border: "2px solid #F07B1D",
            }}
            data-testid="agregation-cta-final"
          >
            <Badge className="bg-[#F07B1D] text-white font-bold mb-5 text-xs">
              🎯 OFFRE PACK COMPLET
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
              Réussissez votre concours avec MGB School
            </h2>
            <p className="text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
              Méthodologie + 10 dissertations rédigées + 10 plans approfondis + 6 auteurs
              + 20 sujets prioritaires + quiz corrigés + certificat MGB School — un pack pensé pour vous mener
              jusqu'à la copie du jour J.
            </p>

            <div className="text-center mb-6">
              <span
                className="font-extrabold leading-none"
                style={{ color: "#F07B1D", fontSize: "56px" }}
              >
                990 €
              </span>
              <p className="text-white/70 text-sm mt-2">
                Paiement unique · Accès immédiat · PDF inclus
              </p>
            </div>

            <a
              href={stripeLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="agregation-cta-final-buy"
            >
              <Button
                className="font-bold text-base text-white hover:brightness-110 px-10 py-6 shadow-lg"
                style={{ background: "#F07B1D" }}
              >
                💳 Acheter maintenant — 990 €
              </Button>
            </a>

            <p className="flex items-center justify-center gap-2 text-white/70 text-xs md:text-sm mt-6">
              <Lock className="w-4 h-4" /> PayPal · CB · Visa · Mastercard ·
              Satisfait ou remboursé 14 jours
            </p>

            <div
              className="mt-6 inline-block px-4 py-2 rounded-lg text-xs md:text-sm"
              style={{
                backgroundColor: "#FFF4E6",
                border: "1px solid #F07B1D",
                color: "#7A3B00",
              }}
            >
              <strong>Financement individuel à ce jour.</strong> Démarches en cours pour l'éligibilité OPCO et France Travail —{" "}
              <a
                href="mailto:contact@mgbschool.fr"
                className="underline font-semibold"
              >
                contact@mgbschool.fr
              </a>
            </div>
          </div>

          {/* Lien fiche détail */}
          <div className="text-center mt-8">
            <Link
              to={`/formations/${formation.slug}`}
              className="text-[#003087] hover:text-[#F07B1D] underline text-sm font-medium"
            >
              Voir la fiche détaillée de la formation
            </Link>
          </div>
        </div>
      </section>
      <div className="container-custom max-w-3xl py-10"><NdaLegalBox /></div>
    </div>
  );
};

export default AgregationPage;
